// services/frontend/src/utils/uploadQueue.ts
// T008: Upload queue management utilities

import { FileUploadItem, BatchUploadState } from '@/types/upload';

/**
 * Maximum number of concurrent uploads allowed
 *
 * Reduced from 5 to 2 to prevent browser network stack saturation
 * with 4-variant generation (2 uploads × 4 variants = 8 operations)
 *
 * @see 042-small-image-variant: Added small variant (800px) to 3-variant system
 */
export const MAX_CONCURRENT_UPLOADS = 2;

/**
 * Calculate derived fields for BatchUploadState
 */
export function calculateBatchState(files: FileUploadItem[]): BatchUploadState {
  const activeCount = files.filter(f => f.state === 'uploading').length;
  const pendingCount = files.filter(f => f.state === 'pending').length;
  const completedCount = files.filter(f => f.state === 'success').length;
  const failedCount = files.filter(f => f.state === 'error').length;
  const totalCount = files.length;

  const overallProgress = totalCount > 0
    ? Math.round(((completedCount + failedCount) / totalCount) * 100)
    : 0;

  const isComplete = activeCount === 0 && pendingCount === 0;
  const hasFailures = failedCount > 0;

  return {
    files,
    activeCount,
    pendingCount,
    completedCount,
    failedCount,
    totalCount,
    overallProgress,
    isComplete,
    hasFailures
  };
}

/**
 * Get the next pending files that can be started based on concurrency limit
 */
export function getNextFilesToStart(
  files: FileUploadItem[],
  maxConcurrent: number = MAX_CONCURRENT_UPLOADS
): FileUploadItem[] {
  const activeCount = files.filter(f => f.state === 'uploading').length;
  const slotsAvailable = Math.max(0, maxConcurrent - activeCount);

  if (slotsAvailable === 0) {
    return [];
  }

  const pending = files.filter(f => f.state === 'pending');
  return pending.slice(0, slotsAvailable);
}

/**
 * Generate a unique ID for a file upload item
 */
export function generateUploadId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Create a new FileUploadItem from a File object
 *
 * @param file - The File object to create an upload item for
 * @param intendedPosition - Optional position for collection uploads. When provided,
 *   this position is assigned at queue time (not completion time) to preserve
 *   the original file selection order.
 */
export function createFileUploadItem(
  file: File,
  intendedPosition: number | null = null
): FileUploadItem {
  return {
    id: generateUploadId(),
    file,
    state: 'pending',
    progress: 0,
    photoId: null,
    error: null,
    filename: file.name,
    size: file.size,
    startedAt: null,
    completedAt: null,
    intendedPosition
  };
}

/**
 * Check if an upload item can be retried
 */
export function canRetryUpload(item: FileUploadItem): boolean {
  return item.state === 'error';
}

/**
 * Check if an upload item can be removed
 */
export function canRemoveUpload(item: FileUploadItem): boolean {
  // Can only remove items that are not currently uploading
  return item.state !== 'uploading';
}

/**
 * Get a user-friendly status message for a file upload
 */
export function getUploadStatusMessage(item: FileUploadItem): string {
  switch (item.state) {
    case 'pending':
      return 'Waiting to upload...';
    case 'uploading':
      return `Uploading... ${item.progress}%`;
    case 'success':
      return 'Upload complete';
    case 'error':
      return item.error || 'Upload failed';
    default:
      return 'Unknown status';
  }
}
